import type { ReactNode } from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';

interface SectionHeadingProps {
  eyebrow: string;
  title: ReactNode;
  description?: string;
  align?: 'center' | 'left';
  className?: string;
}

export default function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'center',
  className = 'mb-12 md:mb-16',
}: SectionHeadingProps) {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.2 });
  const isCenter = align === 'center';

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isCenter ? 'items-center text-center' : 'items-start text-left'} ${className}`}
    >
      <div
        className={`flex items-center gap-3 mb-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <span className="h-px w-8 bg-primary-500/70" aria-hidden="true"></span>
        <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-400 whitespace-nowrap">
          {eyebrow}
        </span>
        <span className="h-px w-8 bg-primary-500/70" aria-hidden="true"></span>
      </div>
      <h2
        className={`text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight transition-all duration-700 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        {title}
      </h2>
      {description && (
        <p
          className={`mt-4 text-foreground-300 text-sm md:text-base leading-relaxed max-w-xl transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          } ${isCenter ? 'mx-auto' : ''}`}
        >
          {description}
        </p>
      )}
    </div>
  );
}